import { type DayNumber, addMonthsDay, startOfMonthDay, toDay, weekdayOfDay } from "@/engine/date";
import { formatDayNumber } from "@/lib/date-format";
import { clampPxPerDay, type TickMode } from "./zoom";

export interface Tick {
  x: number;
  width: number;
  label: string;
  emphasis?: boolean;
}

export interface TimeScale {
  pxPerDay: number;
  tickMode: TickMode;
  startDay: DayNumber;
  endDay: DayNumber; // exclusive
  width: number;
  x: (day: DayNumber) => number;
  dayAt: (px: number) => DayNumber;
  top: Tick[];
  bottom: Tick[];
}

function padDays(mode: TickMode): number {
  if (mode === "day") return 7;
  if (mode === "week") return 21;
  return 45;
}

/** Empty sheet after the last dated item; end snaps to a full week/month cell. */
const RIGHT_PAD_MONTHS = 3;

function snapStart(day: DayNumber, mode: TickMode): DayNumber {
  if (mode === "month") return startOfMonthDay(day);
  return day - weekdayOfDay(day) - 6; // previous Monday
}

/** Exclusive end on a sheet boundary: next month start, or next Monday. */
function snapEndExclusive(day: DayNumber, mode: TickMode): DayNumber {
  if (mode === "month") return addMonthsDay(startOfMonthDay(day), 1);
  const weekday = weekdayOfDay(day);
  const monday = weekday === 1 ? day : day + ((8 - weekday) % 7);
  // A 1–6 day stub month can't hold a week cell or its label — end on that month instead.
  const monthStart = startOfMonthDay(monday);
  return monday > monthStart && monday - monthStart < 7 ? monthStart : monday;
}

export function buildTimeScale(pxPerDayInput: number, minISO: string, maxISO: string, tickMode: TickMode, minWidth = 0): TimeScale {
  const pxPerDay = clampPxPerDay(pxPerDayInput);
  let startDay = snapStart(toDay(minISO) - padDays(tickMode), tickMode);
  let endDay = snapEndExclusive(addMonthsDay(toDay(maxISO), RIGHT_PAD_MONTHS), tickMode);

  let guard = 0;
  while ((endDay - startDay) * pxPerDay < minWidth && guard++ < 36) {
    endDay = tickMode === "month" ? addMonthsDay(endDay, 1) : endDay + 7;
  }

  const width = (endDay - startDay) * pxPerDay;
  const x = (day: DayNumber) => (day - startDay) * pxPerDay;
  const dayAt = (px: number) => startDay + Math.floor(px / pxPerDay);

  const top: Tick[] = [];
  const bottom: Tick[] = [];

  if (tickMode === "month") {
    let d = startDay;
    while (d < endDay) {
      const next = addMonthsDay(d, 1);
      bottom.push({ x: x(d), width: (Math.min(next, endDay) - d) * pxPerDay, label: formatDayNumber(d, "monthName") });
      d = next;
    }
    let y = startDay;
    while (y < endDay) {
      const yr = new Date(y * 86400000).getUTCFullYear();
      const nextYear = Math.floor(Date.UTC(yr + 1, 0, 1) / 86400000);
      const end = Math.min(nextYear, endDay);
      top.push({ x: x(y), width: (end - y) * pxPerDay, label: formatDayNumber(y, "year"), emphasis: true });
      y = end;
    }
  } else {
    let d = startDay;
    while (d < endDay) {
      const next = addMonthsDay(startOfMonthDay(d), 1);
      const end = Math.min(next, endDay);
      top.push({ x: x(d), width: (end - d) * pxPerDay, label: formatDayNumber(d, "yearMonth"), emphasis: true });
      d = end;
    }
    if (tickMode === "day") {
      for (let day = startDay; day < endDay; day++) {
        bottom.push({ x: x(day), width: pxPerDay, label: formatDayNumber(day, "day") });
      }
    } else {
      for (let day = startDay; day < endDay; day += 7) {
        // The month tier above already names the month, so each week cell shows only its Monday's day.
        bottom.push({ x: x(day), width: Math.min(7, endDay - day) * pxPerDay, label: formatDayNumber(day, "day") });
      }
    }
  }

  return { pxPerDay, tickMode, startDay, endDay, width, x, dayAt, top, bottom };
}
